import { Loader2, Paperclip, Send, X } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Textarea } from '@/shared/ui/textarea';

/**
 * Caixa de escrever mensagem, com anexo opcional.
 *
 * COMPONENTE BURRO: só devolve `{ corpo, arquivo }` para quem chamou. Quem
 * envia — e para qual endpoint — é a página, porque solicitante e atendente
 * usam mutations diferentes.
 *
 * Limpa otimisticamente e RESTAURA se `aoEnviar` rejeitar: sem isso, uma falha
 * de rede apaga o que a pessoa acabou de escrever. Mesma escolha do composer
 * de discussões.
 */
export function ComposerDeMensagem({
  aoEnviar,
  enviando,
  placeholder = 'Escreva uma mensagem...',
  extras,
  desabilitado,
}: {
  aoEnviar: (dados: { corpo: string; arquivo: File | null }) => Promise<void>;
  enviando: boolean;
  placeholder?: string;
  /** Controles extras ao lado do botão de enviar (ex.: "aguardar solicitante"). */
  extras?: ReactNode;
  desabilitado?: boolean;
}) {
  const [corpo, setCorpo] = useState('');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const inputArquivo = useRef<HTMLInputElement>(null);

  const vazio = corpo.trim().length === 0 && arquivo === null;

  async function enviar() {
    if (vazio || enviando || desabilitado) return;

    const texto = corpo.trim();
    const anexo = arquivo;

    setCorpo('');
    setArquivo(null);
    if (inputArquivo.current) inputArquivo.current.value = '';

    try {
      // Só com anexo, o corpo vira o nome do arquivo: a API exige `corpo` não
      // vazio, e "📎 print.png" é mais útil na conversa que um balão em branco.
      await aoEnviar({ corpo: texto || `📎 ${anexo?.name ?? 'anexo'}`, arquivo: anexo });
    } catch {
      setCorpo(texto);
      setArquivo(anexo);
    }
  }

  return (
    <div className="space-y-2">
      {arquivo && (
        <div className="flex items-center gap-2 rounded-md border border-brand/20 bg-surface-raised px-3 py-1.5 text-xs">
          <Paperclip className="h-3.5 w-3.5 text-brand" />
          <span className="flex-1 truncate">{arquivo.name}</span>
          <button
            type="button"
            onClick={() => {
              setArquivo(null);
              if (inputArquivo.current) inputArquivo.current.value = '';
            }}
            aria-label="Remover anexo"
            className="text-muted-foreground hover:text-destructive"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <div className="flex items-end gap-2">
        <Textarea
          value={corpo}
          onChange={(e) => setCorpo(e.target.value)}
          // Enter envia, Shift+Enter quebra linha — a convenção de todo chat, e
          // a mesma do composer de discussões.
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void enviar();
            }
          }}
          placeholder={placeholder}
          rows={2}
          disabled={desabilitado}
          className={cn('flex-1 resize-none', desabilitado && 'opacity-60')}
        />

        <input
          ref={inputArquivo}
          type="file"
          accept="image/png,image/jpeg,image/webp,application/pdf"
          className="hidden"
          onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
        />

        <Button
          type="button"
          variant="outline"
          size="icon"
          disabled={desabilitado || enviando}
          onClick={() => inputArquivo.current?.click()}
          aria-label="Anexar arquivo"
        >
          <Paperclip className="h-4 w-4" />
        </Button>

        <Button type="button" onClick={() => void enviar()} disabled={vazio || enviando || desabilitado}>
          {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>

      {extras && <div className="flex items-center gap-2 text-xs">{extras}</div>}
    </div>
  );
}
